import { ConfigService } from '../../config/config.service';
import { DOCUMENT, NgClass } from '@angular/common';
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  Renderer2,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UnsubscribeOnDestroyAdapter } from '@shared';
import {
  LanguageService,
  RightSidebarService,
  InConfiguration,
  Role,
  AuthService,
} from '@core';
import { TranslateModule } from '@ngx-translate/core';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { NgScrollbar } from 'ngx-scrollbar';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';

interface Notifications {
  messageKey: string;
  time: string;
  icon: string;
  color: string;
  status: string;
}

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: true,
  imports: [
    RouterLink,
    NgClass,
    MatButtonModule,
    MatMenuModule,
    NgScrollbar,
    FeatherIconsComponent,
    MatIconModule,
    MatToolbarModule,
    TranslateModule,
  ],
})
export class HeaderComponent
  extends UnsubscribeOnDestroyAdapter
  implements OnInit
{
  public config!: InConfiguration;
  userImg?: string;
  userName?: string;
  schoolName?: string;
  homePage?: string;
  changePasswordLink = '/admin/profile/change-password';
  inboxLink = '/email/inbox';
  profileLink = '/extra-pages/profile';
  isNavbarCollapsed = true;
  flagvalue: string | string[] | undefined;
  countryName: string | string[] = [];
  langStoreValue?: string;
  defaultFlag?: string;
  isOpenSidebar?: boolean;
  docElement?: HTMLElement;
  isFullScreen = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private rightSidebarService: RightSidebarService,
    private configService: ConfigService,
    private authService: AuthService,
    private router: Router,
    public languageService: LanguageService
  ) {
    super();
  }
  listLang = [
    { text: 'Français', flag: 'assets/images/flags/france.svg', lang: 'fr' },
    { text: 'English', flag: 'assets/images/flags/us.svg', lang: 'en' },
    { text: 'العربية', flag: 'assets/images/flags/TN.svg', lang: 'ar' },
  ];
  notifications: Notifications[] = [
    {
      messageKey: 'HEADER.NOTIF_MAIL',
      time: '',
      icon: 'mail',
      color: 'nfc-green',
      status: 'msg-unread',
    },
    {
      messageKey: 'HEADER.NOTIF_INSCRIPTION',
      time: '',
      icon: 'person_add',
      color: 'nfc-blue',
      status: 'msg-read',
    },
    {
      messageKey: 'HEADER.NOTIF_JOURNAL',
      time: '',
      icon: 'auto_stories',
      color: 'nfc-orange',
      status: 'msg-read',
    },
    {
      messageKey: 'HEADER.NOTIF_PAYMENT',
      time: '',
      icon: 'paid',
      color: 'nfc-purple',
      status: 'msg-read',
    },
  ];
  ngOnInit() {
    this.config = this.configService.configData;

    const currentUser = this.authService.currentUserValue;
    if (!currentUser) {
      this.router.navigate(['/authentication/signin']);
      return;
    }

    const userRole = currentUser.role;
    
    // Set user image based on role
    // For SchoolDirector, always use role-based image
    if (userRole === Role.SchoolDirector) {
      this.userImg = 'assets/images/user/school_director.png';
    } else if (currentUser.img && currentUser.img.trim() !== '') {
      this.userImg = currentUser.img;
    } else {
      switch (userRole) {
        case Role.Admin:
          this.userImg = 'assets/images/user/admin.jpg';
          break;
        case Role.Teacher:
          this.userImg = 'assets/images/user/teacher.jpg';
          break;
        case Role.Student:
          this.userImg = 'assets/images/user/user1.jpg';
          break;
        case Role.Parent:
          this.userImg = 'assets/images/user/default_parent.jpeg';
          break;
        case Role.Doctor:
          this.userImg = 'assets/images/user/user3.jpg';
          break;
        default:
          this.userImg = 'assets/images/user/school_director.png';
      }
    }
    
    this.userName = `${currentUser.firstName} ${currentUser.lastName}`;
    this.schoolName = currentUser.schoolName || '';
    this.docElement = document.documentElement;

    if (userRole === Role.Admin || userRole === Role.SchoolDirector) {
      this.homePage = 'admin/dashboard/main';
      this.changePasswordLink = '/admin/profile/change-password';
      this.inboxLink = '/admin/communications';
      this.profileLink = '/extra-pages/profile';
    } else if (userRole === Role.Teacher) {
      this.homePage = 'teacher/dashboard';
      this.changePasswordLink = '/admin/profile/change-password';
      this.inboxLink = '/teacher/messages';
      this.profileLink = '/teacher/dashboard';
    } else if (userRole === Role.Student) {
      this.homePage = 'student/dashboard';
      this.changePasswordLink = '/admin/profile/change-password';
      this.inboxLink = '/email/inbox';
      this.profileLink = '/extra-pages/profile';
    } else if (userRole === Role.Doctor) {
      this.homePage = 'doctor/dashboard';
      this.changePasswordLink = '/doctor/change-password';
      this.inboxLink = '/doctor/dashboard';
      this.profileLink = '/doctor/dashboard';
    } else if (userRole === Role.Parent) {
      this.homePage = 'parent/dashboard';
      this.changePasswordLink = '/parent/change-password';
      this.inboxLink = '/parent/messages';
      this.profileLink = '/parent/profile';
    } else {
      this.homePage = 'admin/dashboard/main';
      this.changePasswordLink = '/admin/profile/change-password';
      this.inboxLink = '/admin/communications';
      this.profileLink = '/extra-pages/profile';
    }

    this.langStoreValue = localStorage.getItem('lang') as string;
    const val = this.listLang.filter((x) => x.lang === this.langStoreValue);
    this.countryName = val.map((element) => element.text);
    if (val.length === 0) {
      if (this.flagvalue === undefined) {
        this.defaultFlag = 'assets/images/flags/us.svg';
      }
    } else {
      this.flagvalue = val.map((element) => element.flag);
    }
  }
  callFullscreen() {
    if (!this.isFullScreen) {
      if (this.docElement?.requestFullscreen != null) {
        this.docElement?.requestFullscreen();
      }
    } else {
      document.exitFullscreen();
    }
    this.isFullScreen = !this.isFullScreen;
  }
  get logoSrc(): string {
    const lang = this.languageService.translate.currentLang || localStorage.getItem('lang') || 'fr';
    return lang === 'ar'
      ? 'assets/images/logos/logo-gardKids-ar-png.png'
      : 'assets/images/logos/logo-gardKids-fr-png.png';
  }
  setLanguage(text: string, lang: string, flag: string) {
    this.countryName = text;
    this.flagvalue = flag;
    this.langStoreValue = lang;
    this.languageService.setLanguage(lang);
  }
  mobileMenuSidebarOpen(event: Event, className: string) {
    const hasClass = (event.target as HTMLInputElement).classList.contains(
      className
    );
    if (hasClass) {
      this.renderer.removeClass(this.document.body, className);
    } else {
      this.renderer.addClass(this.document.body, className);
    }
  }
  callSidemenuCollapse() {
    const hasClass = this.document.body.classList.contains('side-closed');
    if (hasClass) {
      this.renderer.removeClass(this.document.body, 'side-closed');
      this.renderer.removeClass(this.document.body, 'submenu-closed');
      localStorage.setItem('collapsed_menu', 'false');
    } else {
      this.renderer.addClass(this.document.body, 'side-closed');
      this.renderer.addClass(this.document.body, 'submenu-closed');
      localStorage.setItem('collapsed_menu', 'true');
    }
  }
  logout() {
    this.subs.sink = this.authService.logout().subscribe((res) => {
      if (res.success) {
        this.router.navigate(['/authentication/signin']);
      }
    });
  }

  openInbox(): void {
    this.router.navigate([this.inboxLink]);
  }
}
